<div class="credential-types-container">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Credential Types</h2>
      <p class="section-description">Define credential schemas for different integration vendors</p>
    </div>
    <button class="btn btn-primary" (click)="startCreate()" [disabled]="isCreating || isSaving">
      <i class="fas fa-plus"></i> Add Credential Type
    </button>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>
  <div class="message success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fas fa-spinner fa-spin"></i> Loading credential types...
  </div>

  <!-- Filters -->
  <div class="filters-row" *ngIf="!isLoading">
    <div class="search-box">
      <i class="fas fa-search"></i>
      <input type="text" [(ngModel)]="searchQuery" placeholder="Search credential types..." />
    </div>
    <div class="category-filter" *ngIf="categories.length > 1">
      <select [(ngModel)]="selectedCategory">
        <option value="">All Categories</option>
        <option *ngFor="let cat of categories" [value]="cat">{{ cat }}</option>
      </select>
    </div>
  </div>

  <!-- Create Form -->
  <div class="create-form card" *ngIf="isCreating">
    <h3>New Credential Type</h3>
    <div class="form-grid">
      <div class="form-group">
        <label>Name *</label>
        <input type="text" [(ngModel)]="createForm.name" placeholder="e.g., HubSpot API Key" />
      </div>
      <div class="form-group">
        <label>Category</label>
        <select [(ngModel)]="createForm.category">
          <option value="API">API</option>
          <option value="OAuth">OAuth</option>
          <option value="CRM">CRM</option>
          <option value="Email">Email</option>
          <option value="SMS">SMS</option>
          <option value="Cloud">Cloud</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div class="form-group">
        <label>Status</label>
        <select [(ngModel)]="createForm.status">
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>
      <div class="form-group full-width">
        <label>Description</label>
        <textarea [(ngModel)]="createForm.description" placeholder="Describe this credential type..." rows="2"></textarea>
      </div>
      <div class="form-group full-width">
        <label>Field Schema (JSON Schema) *</label>
        <div class="schema-editor">
          <textarea [(ngModel)]="createForm.fieldSchema" rows="12" class="code-textarea"></textarea>
          <button class="btn btn-secondary btn-sm" (click)="formatJson(createForm.fieldSchema)">Format JSON</button>
        </div>
        <small class="help-text">
          Define the fields required for this credential type using JSON Schema.
          Use "properties" to define fields and "required" for mandatory fields.
        </small>
      </div>
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" (click)="cancelCreate()" [disabled]="isSaving">Cancel</button>
      <button class="btn btn-primary" (click)="saveCreate()" [disabled]="isSaving">
        <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
        {{ isSaving ? 'Saving...' : 'Create Credential Type' }}
      </button>
    </div>
  </div>

  <!-- Credential Types List -->
  <div class="credential-types-grid" *ngIf="!isLoading">
    <div class="credential-type-card card" *ngFor="let ctd of filteredCredentialTypes" [class.editing]="ctd.isEditing">
      <!-- View Mode -->
      <ng-container *ngIf="!ctd.isEditing">
        <div class="credential-type-header">
          <div class="credential-type-icon">
            <i class="fas" [ngClass]="getCategoryIcon(ctd.credentialType.Category)"></i>
          </div>
          <div class="credential-type-info">
            <h4>{{ ctd.credentialType.Name }}</h4>
            <div class="credential-type-meta">
              <span class="category-badge">{{ ctd.credentialType.Category }}</span>
              <span class="status-badge" [ngClass]="getStatusClass(ctd.credentialType.Status)">{{ ctd.credentialType.Status }}</span>
            </div>
            <p class="credential-type-description" *ngIf="ctd.credentialType.Description">{{ ctd.credentialType.Description }}</p>
          </div>
        </div>
        <div class="credential-type-footer">
          <div class="schema-info">
            <span class="field-count" *ngIf="ctd.parsedSchema">
              <i class="fas fa-list"></i> {{ getSchemaFieldCount(ctd) }} fields
            </span>
            <span class="field-names" *ngIf="ctd.parsedSchema" title="{{ getSchemaFieldNames(ctd) }}">
              {{ getSchemaFieldNames(ctd) }}
            </span>
          </div>
          <div class="credential-type-actions">
            <button class="btn btn-icon" title="Edit" (click)="startEdit(ctd)" [disabled]="isSaving">
              <i class="fas fa-edit"></i>
            </button>
            <button class="btn btn-icon btn-danger" title="Delete" (click)="confirmDelete(ctd)" [disabled]="isSaving">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </ng-container>

      <!-- Edit Mode -->
      <ng-container *ngIf="ctd.isEditing">
        <div class="edit-form">
          <h4>Edit Credential Type</h4>
          <div class="form-group">
            <label>Name</label>
            <input type="text" [(ngModel)]="ctd.editForm.name" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Category</label>
              <select [(ngModel)]="ctd.editForm.category">
                <option value="API">API</option>
                <option value="OAuth">OAuth</option>
                <option value="CRM">CRM</option>
                <option value="Email">Email</option>
                <option value="SMS">SMS</option>
                <option value="Cloud">Cloud</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="form-group">
              <label>Status</label>
              <select [(ngModel)]="ctd.editForm.status">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea [(ngModel)]="ctd.editForm.description" rows="2"></textarea>
          </div>
          <div class="form-group">
            <label>Field Schema (JSON Schema)</label>
            <textarea [(ngModel)]="ctd.editForm.fieldSchema" rows="10" class="code-textarea"></textarea>
          </div>
          <div class="form-actions">
            <button class="btn btn-secondary" (click)="cancelEdit(ctd)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(ctd)" [disabled]="isSaving">
              <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
              Save
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Empty State -->
    <div class="empty-state card" *ngIf="filteredCredentialTypes.length === 0 && !isCreating">
      <i class="fas fa-puzzle-piece"></i>
      <p>No credential types defined</p>
      <p class="empty-hint">Create credential types to define the fields needed for different integrations</p>
      <button class="btn btn-primary" (click)="startCreate()">Add Your First Credential Type</button>
    </div>
  </div>

  <!-- Confirm Dialog -->
  <div class="modal-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDialogYes()" [disabled]="isSaving">
          <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
          Delete
        </button>
      </div>
    </div>
  </div>
</div>
